import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import pluralize from "pluralize";

import {
    LottieLoadingIndicator, FlexContainer, Button,
    StandardModal,
    StandardModalActionGroup,
    StandardModalSection
} from "src/components";

import {
    Card, Label, Number, BottomErrorMessage, TableTemplate
} from "./EditUserList";

import {
    // useEffect
    getRemainingLicense, countNewUsers,
    // CsvListContainer
    saveAllDirty,
    // ImportUsersInputField
    ClearUpload, InputField, firstNameErrors, lastNameErrors, emailErrors, phoneErrors, checkPropertyFieldIsEmptyAddError, propertyFieldIsNotEmptyRemoverError, repositionErrorMessageIndexes, Tr
} from "./EditUserList";

import {
    CountryPhoneInput
} from "src/components";
import { SubscriptionMode } from "src/constants/userDetails";
import { getCsvImportErrorMessages } from "./csvImportErrors";
import { doesUserCountExceedAvailableLicenses } from "./licenseLimit";

const EditUserListModal = (props) => {
    const { uploadUsers, showDialog, handleClose, isClient, openUploadUserListModal, uploadCsvPostEmployees, newUser, token, fetchingCsv, countryCode, existingUserEmailsResponse, customNames, companyMode } = props;
    const [importUsers, setImportUsers] = useState(uploadUsers);
    const [errorList, setErrorList] = useState([]);
    const [totalLicense, setTotalLicense] = useState(0);
    const [isAmountOfUserExceeds, setIsAmountOfUserExceeds] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [existingEmails, setExistingEmails] = useState({ "existingEmployeeEmails": [], "existingClientEmails": [] });
    const [totalNewUsersInCsvList, setTotalNewUsersInCsvList] = useState(0);
    const [hasExistedUser, setHasExistedUser] = useState(false); 

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            
            const remainingLicense = await getRemainingLicense(token);
            setTotalLicense(remainingLicense);

            setExistingEmails(existingUserEmailsResponse);

            const countNewUsersTotal = countNewUsers(importUsers, existingUserEmailsResponse);
            setTotalNewUsersInCsvList(countNewUsersTotal);

            //Total New Users in CSV List
            setIsAmountOfUserExceeds(doesUserCountExceedAvailableLicenses(
                companyMode,
                remainingLicense,
                countNewUsersTotal
            ));

            setIsLoading(false);
        })();
    }, []);

    const handleModalClose = (_, reason) => {
        if (reason !== "backdropClick") {
            handleClose();
        }
    };

    return (
        <React.Fragment>
            <StandardModal
                open={showDialog}
                onClose={handleModalClose}
                widthMode="table"
                title={`${isClient ? `${pluralize(customNames.clients)}` : `${pluralize(customNames.employees)}` } CSV User List`}
                description={`Please review your uploaded list of ${pluralize(customNames.clients)} or ${pluralize(customNames.employees)} to ensure accuracy. Double-check details before confirming your submission.`}
                paperStyle={{
                    minHeight: "30em",
                }}
            >
                {
                    isLoading ? <LoadingContainer/> :
                        <CSVListContainer 
                            isClient={isClient}
                            importUsers={importUsers}
                            setImportUsers={setImportUsers}
                            totalLicense={totalLicense}
                            totalNewUsersInCsvList={totalNewUsersInCsvList}
                            setErrorList={setErrorList}

                            newUser={newUser}
                            saveAllDirty={() => { saveAllDirty(setErrorList, importUsers, newUser, isClient, isAmountOfUserExceeds, uploadCsvPostEmployees, handleClose); } }
                            handleClose={handleClose}
                            openUploadUserListModal={openUploadUserListModal}
                            fetchingCsv={fetchingCsv}
                            isAmountOfUserExceeds={isAmountOfUserExceeds}
                            //importUsersInputFields
                            existingEmails={existingEmails}
                            errorList={errorList}
                            setTotalNewUsersInCsvList={setTotalNewUsersInCsvList}
                            countNewUsers={countNewUsers}
                            setIsAmountOfUserExceeds={setIsAmountOfUserExceeds}
                            countryCode={countryCode}
                            hasExistedUser={hasExistedUser}
                            setHasExistedUser={setHasExistedUser}
                            customNames={customNames}
                            companyMode={companyMode}
                        />
                }
            </StandardModal>
        </React.Fragment>
    );
};

function LoadingContainer() {
    return (
        <div style={{ padding: "0px 55px 0 55px" }}>
            <Box>
                <FlexContainer direction="row" justifyContent="space-between" marginTop="10em"
                    style={{ maxWidth: "98.625rem" }}
                >
                    <LottieLoadingIndicator/>
                </FlexContainer>
            </Box>
        </div>
    );
}


function CSVListContainer(props) {
    const { 
        isClient,
        importUsers, 
        setImportUsers, 
        totalLicense, 
        totalNewUsersInCsvList,
        errorList, 
        isAmountOfUserExceeds, 
        handleClose, 
        openUploadUserListModal, 
        fetchingCsv, 
        setErrorList, 
        saveAllDirty, 
        setTotalNewUsersInCsvList, 
        setIsAmountOfUserExceeds, 
        countNewUsers, 
        countryCode, 
        hasExistedUser, 
        setHasExistedUser,
        companyMode
    } = props;
    const { existingEmails } = props; //importUsersInputFields
    const totalUsersInCsvList = importUsers.length.toLocaleString("en-US");
    const userLicensesAvailable = companyMode === SubscriptionMode.DEMO ? "-" : totalLicense.toLocaleString();

    const [isShowButtonWhenWrongUserType, setIsShowButtonWhenWrongUserType] = useState(false);

    function handleConfirm() {  
        setErrorList([]);
    
        if (!(importUsers.length > 0)) {
            setErrorList((prevErrorList) => [...prevErrorList, "* There is no user list to upload."]);
            return;
        }
    
        saveAllDirty();
    }

    return (
        <div style={{ display: "grid", gap: "1rem", minWidth: 0 }}>
            <Box>
                <StandardModalSection>
                    <FlexContainer tDirection="row" direction="row" margin="0"
                        width="100%"
                        justifyContent="end"
                        style={{ gap: "1rem", flexWrap: "wrap" }}
                    >
                        <Card style={{ marginRight: "1em" }}>
                            <Label>Total Users in CSV List</Label>
                            <Number>{ totalUsersInCsvList }</Number>
                        </Card>
                        <Card style={{ marginRight: "1em", border: isAmountOfUserExceeds ? "1px solid #FF5353" : "inherit", color: isAmountOfUserExceeds ? "#FF5353" : "inherit" }}>
                            <Label>Total New Users in CSV List</Label>
                            <Number>{ totalNewUsersInCsvList }</Number>
                        </Card>
                        <Card>
                            <Label>User Licenses Available</Label>
                            <Number>{ userLicensesAvailable }</Number>
                        </Card>
                    </FlexContainer>
                </StandardModalSection>
            </Box>

            <TableTemplate isClient={isClient} >
                <ImportUsersInputFields
                    isClient={isClient}
                    importUsers={importUsers} 
                    setImportUsers={setImportUsers}  
                    existingEmails={existingEmails} 
                    errorList={errorList} 
                    setTotalNewUsersInCsvList={setTotalNewUsersInCsvList} 
                    setIsAmountOfUserExceeds={setIsAmountOfUserExceeds} 
                    totalLicense={totalLicense}
                    countNewUsers={countNewUsers} 
                    countryCode={countryCode}
                    isShowButtonWhenWrongUserType={isShowButtonWhenWrongUserType}
                    setIsShowButtonWhenWrongUserType={setIsShowButtonWhenWrongUserType}
                    hasExistedUser={hasExistedUser}
                    setHasExistedUser={setHasExistedUser}
                    companyMode={companyMode}
                />
            </TableTemplate>

            <ErrorMessage 
                errorList={errorList} 
                isAmountOfUserExceeds={isAmountOfUserExceeds}
                isClient={isClient}
                isShowButtonWhenWrongUserType={isShowButtonWhenWrongUserType}
                hasExistedUser={hasExistedUser}
            />

            <Box display="flex" justifyContent="right" margin="auto">
                <StandardModalActionGroup $pushRight>
                    <Button
                        border="1px solid #000"
                        color="#000"
                        marginRight="15px"
                        width="140px"
                        onClick={ () => [handleClose(), openUploadUserListModal()]}
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                        className="form-btns disabled"
                    >
                            Back
                    </Button>
                    { (fetchingCsv > 0) && <LottieLoadingIndicator width="2em" height="2em" containerHeight="2em" margin="0" /> }
                    { (fetchingCsv === 0) &&
                            <Button backgroundColor="#171A22" color="#fff"
                                border="1px solid #171A22"
                                width="140px"
                                style={{ borderRadius: "7px", boxShadow: "none" }}
                                type="submit"
                                onClick={() => handleConfirm() }
                                className="form-btns disabled"
                            >
                            Confirm
                            </Button>
                    }
                </StandardModalActionGroup>
            </Box>
        </div>
    );
}

const ImportUsersInputFields = ({ importUsers, setImportUsers, existingEmails, isClient, errorList, setTotalNewUsersInCsvList, countNewUsers, setIsAmountOfUserExceeds, totalLicense, countryCode, setIsShowButtonWhenWrongUserType, setHasExistedUser, companyMode }) => {
    const firstNameErrorList = firstNameErrors(errorList);
    const lastNameErrorList = lastNameErrors(errorList);
    const emailErrorList = emailErrors(errorList);

    const phoneErrorList = phoneErrors(errorList);

    function clearUploadOnClickHandler(index) {
        repositionErrorMessageIndexes(index, errorList, isClient);

        //remove that row in importUsers
        const updatedImportUsers = importUsers.filter((_, objIndex) => objIndex !== index);
        setImportUsers(updatedImportUsers);
    
        //Total New Users in CSV List
        const countNewUsersTotal = countNewUsers(updatedImportUsers, existingEmails);
        setTotalNewUsersInCsvList(countNewUsersTotal);
        setIsAmountOfUserExceeds(doesUserCountExceedAvailableLicenses(
            companyMode,
            totalLicense,
            countNewUsersTotal
        ));
    }

    function onChangeHandler(e, index, propertyField) {
        const updatedImportUsers = [...importUsers];
    
        updatedImportUsers[index] = { 
            ...updatedImportUsers[index], 
            [propertyField]: e.target.value 
        };
        
        if (propertyField === "email" 
            || propertyField === "firstName" 
            || (!isClient && propertyField === "lastName")) {
            if (!checkPropertyFieldIsEmptyAddError(e, index, propertyField, errorList)) {
                propertyFieldIsNotEmptyRemoverError(e, index, propertyField, errorList);
            }
        }
    
        setImportUsers(updatedImportUsers);

        //Total New Users in CSV List
        const countNewUsersTotal = countNewUsers(updatedImportUsers, existingEmails);
        setTotalNewUsersInCsvList(countNewUsersTotal);
        setIsAmountOfUserExceeds(doesUserCountExceedAvailableLicenses(
            companyMode,
            totalLicense,
            countNewUsersTotal
        ));
    }

    function handlePhoneNumber(phone, index, propertyField) {
        const updatedUsers = [...importUsers];
        
        if (phone !== "" && !phone.startsWith("+")) {
            phone = "+" + phone;
        }
    
        updatedUsers[index] = { 
            ...updatedUsers[index], 
            [propertyField]: phone
        };
    
        setImportUsers(updatedUsers);
    }

    function mapImportUsers() {
        let hasShownButtonWhenWrongUserType = false;
        let existedUserBool = false;

        const importUsersMapped = importUsers.map((user, index) => {
            const rowId = user.newUser ? "newUser_" + index : user.uuid;
                
            const isFirstNameInvalid = firstNameErrorList.includes(index);
            const isLastNameInvalid = lastNameErrorList.includes(index);
            const isEmailInvalid = emailErrorList.includes(index);
            const isPhoneInvalid = phoneErrorList.includes(index);
            
            const isExistingEmployee = existingEmails["existingEmployeeEmails"].includes(user.email);
            const isExistingClient = existingEmails["existingClientEmails"].includes(user.email);
            const isNewUser = !isExistingEmployee && !isExistingClient;
            const showButtonWhenWrongUserType = (isClient && isExistingEmployee) || (!isClient && isExistingClient);
            
            // Check if we are displaying the wrong user type
            hasShownButtonWhenWrongUserType = hasShownButtonWhenWrongUserType || showButtonWhenWrongUserType;

            //check if there was an existing user from the given list
            existedUserBool = existedUserBool || !isNewUser;

            return (
                <Tr key={rowId} isExistingUser={!(isNewUser || showButtonWhenWrongUserType)} showButtonWhenWrongUserType={showButtonWhenWrongUserType} >
                    <td>{ (isNewUser || showButtonWhenWrongUserType) && <ClearUpload onClick={ () => clearUploadOnClickHandler(index) }></ClearUpload> } </td>
                    <td><InputField value={user.firstName} errors={isFirstNameInvalid} onChangeHandler={(e) => onChangeHandler(e, index, "firstName")} /></td>
                    <td><InputField value={user.lastName} errors={isLastNameInvalid} onChangeHandler={(e) => onChangeHandler(e, index, "lastName")} /></td>
                    <td><InputField value={user.email} errors={isEmailInvalid} onChangeHandler={(e) => onChangeHandler(e, index, "email")} width="250px" /></td>
                    <td>
                        <CountryPhoneInput
                            autoFormat={false}
                            prefix={"+"}
                            error={isPhoneInvalid} //boolean
                            country={countryCode.toLowerCase()}
                            placeholder=""
                            value={user.phoneNumber}
                            onChange={(phone) => handlePhoneNumber(phone, index, "phoneNumber")}
                        />
                    </td>
                    {!isClient && <td><InputField value={user.department} errors={false} onChangeHandler={(e) => onChangeHandler(e, index, "department")} /></td>}
                    <td><InputField value={user.employmentPosition} errors={false} onChangeHandler={(e) => onChangeHandler(e, index, "employmentPosition")} /></td>
                </Tr>
            );
        });

        setIsShowButtonWhenWrongUserType(hasShownButtonWhenWrongUserType);     
        setHasExistedUser(existedUserBool);

        return importUsersMapped;
    }
    
    const mappedUsers = mapImportUsers();
    return mappedUsers;    
};

function ErrorMessage({ errorList, isAmountOfUserExceeds, isShowButtonWhenWrongUserType, isClient, hasExistedUser }) {
    const apiErrorMessages = Array.isArray(errorList)
        ? errorList.filter((error) => typeof error === "string")
        : getCsvImportErrorMessages(errorList);

    const showMessageWhenUserExist = <div style={{ color: "#171A22" }}>Existing Users or Updated User Information: Blue-highlighted rows indicate existing users; no licenses will be deducted.</div>;
    const noMoreAvailableUsers = <BottomErrorMessage>* The amount of users in this list exceeds the amount of available licenses. Please purchases additional licenses to continue or remove the required amount of users to match your available license.</BottomErrorMessage>;
    
    //when isShowButtonWhenWrongUserType is true
    const showMessageWhenWrongUserTypeEmployee = <BottomErrorMessage>User Exists as Client: 1 or more of the Employees in this list already exists as a ‘Client’ using the same email or mobile number.</BottomErrorMessage>;
    const showMessageWhenWrongUserTypeClient = <BottomErrorMessage>User Exists as Employee: 1 or more of the Clients in this list already exists as an ‘Employee’ using the same email or mobile number.</BottomErrorMessage>;
    
    return (
        <div style={{ width: "60%" }}>
            {hasExistedUser && showMessageWhenUserExist}
            {apiErrorMessages.map((message, index) => (
                <BottomErrorMessage key={`${message}-${index}`}>{message}</BottomErrorMessage>
            ))}
            {isShowButtonWhenWrongUserType && !isClient && showMessageWhenWrongUserTypeEmployee}
            {isShowButtonWhenWrongUserType && isClient && showMessageWhenWrongUserTypeClient}
            {isAmountOfUserExceeds && noMoreAvailableUsers}
            
        </div>
    );
}

export default EditUserListModal;
